/**
 * Tool settings — the one reader and the one key style for a session's
 * `tool_config`.
 *
 * Before this, the session wizard wrote `{ dotsPerParticipant }` and
 * `{ budgetPercentage }` (camelCase), the engine adapters in `src/tools/` read
 * `dots_per_participant` / `budget_percentage` / `pareto_threshold`
 * (snake_case), and the dot voting, buy-a-feature and Pareto pages read
 * neither — they hard-coded 5 dots, a 40% budget and a 20% top band. So a
 * facilitator's choice in the wizard was saved and then ignored everywhere.
 *
 * The rule now:
 *  - The engine's key style (snake_case) is the stored style. Writers call
 *    `normalizeToolConfig` before saving; it also accepts the old camelCase
 *    spellings so rows written before this change still read correctly.
 *  - Every reader — the tool pages and the engine adapters — calls
 *    `readToolSettings`, which applies the same normalization and defaults.
 *
 * The scoring scale (2026-10-29): a method that scores options against
 * criteria (simple weighted scoring) scored every session 1–5, because no
 * setup screen asked and the scoring screen forced 1–5. The facilitator now
 * picks it at setup from SCORING_SCALE_CHOICES; it is saved here as
 * `scoring_scale_min` / `scoring_scale_max` (the engine adapter's own keys),
 * and the scoring screen, the engine and a guest's screen all read it back
 * through `readToolSettings`.
 */
import { DEFAULT_SESSION_CONFIG, SCORING_SCALES } from './semanticDefaults';

/** Stored (engine) key → older camelCase spelling still found in saved rows. */
const LEGACY_KEY_ALIASES: Record<string, string> = {
  dots_per_participant: 'dotsPerParticipant',
  budget_percentage: 'budgetPercentage',
  pareto_threshold: 'paretoThreshold',
  num_participants: 'numParticipants',
  scoring_scale_min: 'scoringScaleMin',
  scoring_scale_max: 'scoringScaleMax',
};

/** A whole-number scale people score on, lowest and highest value. */
export interface ScoringScaleRange {
  min: number;
  max: number;
}

/** A scale offered at setup, with the word that says what it is for. */
export interface ScoringScaleChoice extends ScoringScaleRange {
  hint: string;
}

/**
 * The scales a facilitator picks from when a method scores options against
 * criteria: the workspace's standard whole-number scales (semanticDefaults),
 * shortest first. The recommended one is DEFAULT_SCORING_SCALE.
 */
export const SCORING_SCALE_CHOICES: readonly ScoringScaleChoice[] = [
  { min: SCORING_SCALES.THREE_POINT.min, max: SCORING_SCALES.THREE_POINT.max, hint: 'Simple' },
  { min: SCORING_SCALES.FIVE_POINT.min, max: SCORING_SCALES.FIVE_POINT.max, hint: 'Standard' },
  { min: SCORING_SCALES.SEVEN_POINT.min, max: SCORING_SCALES.SEVEN_POINT.max, hint: 'Detailed' },
  { min: SCORING_SCALES.TEN_POINT.min, max: SCORING_SCALES.TEN_POINT.max, hint: 'Fine-grained' },
];

/** The scale a session scores on when nobody chose one: the workspace default (five-point). */
export const DEFAULT_SCORING_SCALE: ScoringScaleRange = {
  min: SCORING_SCALES[DEFAULT_SESSION_CONFIG.defaultScoringScale].min,
  max: SCORING_SCALES[DEFAULT_SESSION_CONFIG.defaultScoringScale].max,
};

/** More values than this is not a scale a person can score on one screen. */
const MAX_SCALE_VALUES = 11;

export const TOOL_SETTING_DEFAULTS = {
  /** Dot voting: dots each participant may place. */
  dotsPerParticipant: 5,
  /** Buy-a-feature: share of the total item price pool handed out as budget (0–1). */
  budgetPercentage: 0.4,
  /** Pareto: share of items that make up the "vital few" band (0–1). */
  paretoThreshold: 0.2,
  /** Weighted scoring: the scale each option is scored on, per criterion. */
  scoringScale: DEFAULT_SCORING_SCALE,
} as const;

export interface ToolSettings {
  dotsPerParticipant: number;
  budgetPercentage: number;
  paretoThreshold: number;
  scoringScale: ScoringScaleRange;
}

/**
 * Return a copy of `raw` with every known setting under its engine key.
 * Unknown keys pass through untouched. When both spellings exist, the engine
 * spelling wins.
 */
export function normalizeToolConfig(
  raw: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  if (!raw || typeof raw !== 'object') return {};
  const out: Record<string, unknown> = { ...raw };
  for (const [engineKey, legacyKey] of Object.entries(LEGACY_KEY_ALIASES)) {
    if (legacyKey in out) {
      if (out[engineKey] === undefined) out[engineKey] = out[legacyKey];
      delete out[legacyKey];
    }
  }
  return out;
}

function positiveNumber(value: unknown): number | undefined {
  const n = typeof value === 'string' ? Number(value) : value;
  return typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : undefined;
}

/** A share between 0 and 1. A whole-number percentage (e.g. 40) is read as 40%. */
function share(value: unknown): number | undefined {
  const n = positiveNumber(value);
  if (n === undefined) return undefined;
  if (n <= 1) return n;
  if (n <= 100) return n / 100;
  return undefined;
}

function wholeNumber(value: unknown): number | undefined {
  const n = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  return typeof n === 'number' && Number.isInteger(n) && n >= 0 ? n : undefined;
}

/** A saved scale, or undefined when it is missing or not a scale a person can score on. */
function parseScoringScale(minRaw: unknown, maxRaw: unknown): ScoringScaleRange | undefined {
  const min = wholeNumber(minRaw);
  const max = wholeNumber(maxRaw);
  if (min === undefined || max === undefined || max <= min) return undefined;
  if (max - min + 1 > MAX_SCALE_VALUES) return undefined;
  return { min, max };
}

/** Read a session's tool settings, with defaults for anything unset or invalid. */
export function readToolSettings(raw: Record<string, unknown> | null | undefined): ToolSettings {
  const cfg = normalizeToolConfig(raw);
  const dots = positiveNumber(cfg.dots_per_participant);
  return {
    dotsPerParticipant: dots !== undefined ? Math.round(dots) : TOOL_SETTING_DEFAULTS.dotsPerParticipant,
    budgetPercentage: share(cfg.budget_percentage) ?? TOOL_SETTING_DEFAULTS.budgetPercentage,
    paretoThreshold: share(cfg.pareto_threshold) ?? TOOL_SETTING_DEFAULTS.paretoThreshold,
    scoringScale:
      parseScoringScale(cfg.scoring_scale_min, cfg.scoring_scale_max) ?? { ...TOOL_SETTING_DEFAULTS.scoringScale },
  };
}

/** The tool settings a setup screen saves for the scale it was given (engine keys). */
export function scoringScaleToolConfig(scale: ScoringScaleRange): Record<string, number> {
  return { scoring_scale_min: scale.min, scoring_scale_max: scale.max };
}

/** How a scale reads in a sentence or a heading: "1–5". */
export function scoringScaleLabel(scale: ScoringScaleRange): string {
  return `${scale.min}–${scale.max}`;
}

export function isSameScoringScale(a: ScoringScaleRange, b: ScoringScaleRange): boolean {
  return a.min === b.min && a.max === b.max;
}
